import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { getEntityTemplates } from '../../services/dtServices'
import { AttributeType } from '../../constants/constants'
import {
    fetchProgramByID,
    fetchProgramTypes,
} from '../../services/programServices'
import { updateLoader } from './loaderSlice'
import { ProgramAttributes } from '../../components/Programs/constants'
import { IListItemValue } from '../../interfaces/Interfaces'

interface ProgramStateType {
    fields: ProgramField[]
    templatesData: ProgramTemplatesType[]
    ProgramTypeListData: ProgramTypeListData[]
}
export interface ProgramField {
    id: string
    name: string
    dataType: AttributeType
    width: string
    required: boolean
    value: any
    list?: any[]
    errorMessage?: string
    disabled?: boolean
    excludeOrgs?: string[]
}

type UpdateProgramField = {
    id: string
    value:
        | string
        | number
        | boolean
        | string[]
        | IListItemValue
        | IListItemValue[] // or a specific union if needed
}

type UpdateProgramTemplate = {
    categoryId: string
    id: string
    templateId: string
    value: boolean
}

export type ProgramTemplatesType = {
    categoryId: string
    categoryName: string
    categoryTemplates: CategoryTemplates[]
    categoryDefaultAndDisabledTemplates: CategoryTemplates[]
}

type CategoryTemplates = {
    id: string
    name: string
    required: boolean
    templates: PTemplates[]
    selectedTemplates: string[]
}

export type PTemplates = {
    id: string
    name: string
    subEntityTypes: PTemplates[]
    [key: string]: any
}

type ProgramTypeListData = {
    id: string
    name: string
    configurations: ProgramTemplatesType[]
}

const programFields: ProgramField[] = [
    {
        id: ProgramAttributes.NAME,
        name: 'Name',
        dataType: AttributeType.STRING,
        width: '100%',
        required: true,
        errorMessage: '',
        value: '',
    },
    {
        id: ProgramAttributes.PROGRAMTYPE,
        name: 'Program Type',
        list: [],
        dataType: AttributeType.SELECT,
        width: '100%',
        required: true,
        errorMessage: '',
        value: '',
    },
    {
        id: ProgramAttributes.OWNER,
        name: 'Buyer',
        list: [],
        dataType: AttributeType.ORGANIZATION_SELECT,
        width: '100%',
        required: true,
        errorMessage: '',
        value: '',
        excludeOrgs: [],
    },
    {
        id: ProgramAttributes.SUBSCRIBER,
        name: 'Shipper',
        list: [],
        dataType: AttributeType.ORGANIZATION_MULTI_SELECT,
        width: '100%',
        required: false,
        errorMessage: '',
        value: [],
        excludeOrgs: [],
    },
    {
        id: ProgramAttributes.CARRIER,
        name: 'Carrier',
        list: [],
        dataType: AttributeType.ORGANIZATION_MULTI_SELECT,
        width: '100%',
        required: false,
        errorMessage: '',
        value: [],
        excludeOrgs: [],
    },
    {
        id: ProgramAttributes.RECEIVER,
        name: 'Receiver',
        list: [],
        dataType: AttributeType.ORGANIZATION_MULTI_SELECT,
        width: '100%',
        required: false,
        errorMessage: '',
        value: [],
        excludeOrgs: [],
    },
    {
        id: ProgramAttributes.CUSTOMER,
        name: 'Customer',
        list: [],
        dataType: AttributeType.ORGANIZATION_MULTI_SELECT,
        width: '100%',
        required: false,
        errorMessage: '',
        value: [],
        excludeOrgs: [],
    },
]

const initialState: ProgramStateType = {
    fields: programFields,
    templatesData: [],
    ProgramTypeListData: [],
}

export const setProgramData = createAsyncThunk(
    'program/setProgramData',
    async (instanceId: string | null, thunkAPI) => {
        const { dispatch } = thunkAPI
        dispatch(updateLoader(true))
        try {
            const [response, programTypesList] = await Promise.all([
                fetchProgramByID(instanceId),
                fetchProgramTypes(),
            ])
            const programTypeId: string = instanceId
                ? response?.programTypeId
                : programTypesList[0]?.programTypeId || ''

            const updatedProgramTypeListData: any[] =
                programTypesList?.map((item) => {
                    const updatedConfigurations = item.configurations.map(
                        (c) => {
                            return {
                                categoryId: c.categoryId,
                                categoryName: c.categoryName,
                                categoryTemplates:
                                    c.selectable
                                        ?.filter((i) => i.selectable)
                                        ?.map((i) => {
                                            return {
                                                id: i.id,
                                                name: i.name,
                                                required: i.required,
                                                templates: [
                                                    {
                                                        id: i.id,
                                                        name: i.name,
                                                        required: i.required,
                                                        errorMessage: '',
                                                    },
                                                ],
                                                selectedTemplates:
                                                    response?.templates?.find(
                                                        (t: any) =>
                                                            t.categoryId ===
                                                                c.categoryId &&
                                                            t.id === i.name,
                                                    )?.selectedTemplates || [],
                                            }
                                        }) || [],
                                categoryDefaultAndDisabledTemplates:
                                    c.selectable
                                        ?.filter((i) => !i.selectable)
                                        ?.map((i) => {
                                            return {
                                                id: i.id,
                                                name: i.name,
                                                templates: [
                                                    { id: i.id, name: i.name },
                                                ],
                                                selectedTemplates: [i.id],
                                            }
                                        }) || [],
                            }
                        },
                    )
                    return {
                        configurations: updatedConfigurations,
                        id: item.programTypeId,
                        name: item.programTypeName,
                    }
                }) || []

            const updatedProgramFields = programFields?.map((field) => {
                const updatedField = { ...field }
                if (field.id === ProgramAttributes.NAME) {
                    updatedField.value = response?.name
                } else if (field.id === ProgramAttributes.PROGRAMTYPE) {
                    updatedField.value = programTypeId
                    updatedField.list = updatedProgramTypeListData
                } else if (field.id === ProgramAttributes.OWNER) {
                    updatedField.value = response?.owner
                    updatedField.disabled = !!instanceId
                } else if (field.id === ProgramAttributes.SUBSCRIBER) {
                    updatedField.value = response?.subscribers || []
                    updatedField.excludeOrgs = [response?.owner?.id]
                } else if (field.id === ProgramAttributes.CARRIER) {
                    updatedField.value = response?.carriers || []
                    updatedField.excludeOrgs = [response?.owner?.id]
                } else if (field.id === ProgramAttributes.RECEIVER) {
                    updatedField.value = response?.receivers || []
                    updatedField.excludeOrgs = [response?.owner?.id]
                } else if (field.id === ProgramAttributes.CUSTOMER) {
                    updatedField.value = response?.customers || []
                    updatedField.excludeOrgs = [response?.owner?.id]
                }

                return updatedField
            })
            await dispatch(
                updateProgramTemplatesWithSubTemplates({
                    orgId: response?.owner?.id || '',
                    programTypeId,
                    programTypeListData: updatedProgramTypeListData,
                    refresh: false,
                }),
            )

            return { updatedProgramFields, updatedProgramTypeListData }
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        } finally {
            dispatch(updateLoader(false))
        }
    },
)

export const updateProgramTemplatesWithSubTemplates = createAsyncThunk(
    'program/updateProgramTemplatesWithSubTemplates',
    async (
        {
            orgId,
            programTypeId,
            programTypeListData,
            refresh,
        }: {
            orgId: string
            programTypeId: string
            programTypeListData: ProgramTypeListData[]
            refresh: boolean
        },
        thunkAPI,
    ) => {
        const { dispatch } = thunkAPI
        dispatch(updateLoader(true))
        try {
            let response: any[] = []
            if (orgId) {
                response = await getEntityTemplates(orgId)
            }
            const selectedProgramTypeConfigurations =
                programTypeListData?.find((i) => i.id === programTypeId)
                    ?.configurations || []
            const updatedProgramTemplates: ProgramTemplatesType[] = JSON.parse(
                JSON.stringify(selectedProgramTypeConfigurations),
            )
            updatedProgramTemplates.forEach((category) => {
                category.categoryTemplates.forEach((item) => {
                    item.templates.forEach((i) => {
                        const subTemplates = findSubEntityTypesById(
                            i.id,
                            response || [],
                        )
                        i.subEntityTypes =
                            (subTemplates as unknown as PTemplates[]) ||
                            ([] as unknown as PTemplates[])
                    })
                    // const allSubTemplateIds = getAllTemplateIds(item.templates)
                    if (refresh) {
                        // item.selectedTemplates =
                        //     item.selectedTemplates?.filter((i) =>
                        //         allSubTemplateIds?.includes(i),
                        //     ) || []
                        item.selectedTemplates = []
                    }
                })
            })

            return updatedProgramTemplates
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        } finally {
            dispatch(updateLoader(false))
        }
    },
)

const findSubEntityTypesById = (id: string, templates: any[]): any[] | null => {
    for (const entity of templates) {
        if (entity.id === id) {
            return entity.subEntityTypes
        }
        if (entity.subEntityTypes && entity.subEntityTypes.length > 0) {
            const result = findSubEntityTypesById(id, entity.subEntityTypes)
            if (result !== null) {
                return result
            }
        }
    }
    return null
}

// keeping this for future use
// const getAllTemplateIds = (templates: any[]): string[] => {
//     const result: string[] = []
//     for (const t of templates) {
//         result.push(t.id)
//         if (t.subEntityTypes?.length > 0) {
//             result.push(...getAllTemplateIds(t.subEntityTypes))
//         }
//     }
//     return result
// }

const getNestedIdsById = (data: any[], targetId: string): string[] => {
    const result: string[] = []

    const collectAllIds = (node: any) => {
        result.push(node.id)
        node.subEntityTypes?.forEach(collectAllIds)
    }

    const findNode = (nodes: any[]) => {
        for (const node of nodes) {
            if (node.id === targetId) {
                collectAllIds(node)
                return true
            }
            if (node.subEntityTypes && findNode(node.subEntityTypes))
                return true
        }
        return false
    }

    findNode(data)
    return result
}

const programSlice = createSlice({
    name: 'program',
    initialState,
    reducers: {
        updateProgramFields(state, action) {
            state.fields = action.payload
        },
        updateProgramTemplates(state, action) {
            state.templatesData = action.payload
        },
        updateProgramField: (
            state,
            action: PayloadAction<UpdateProgramField>,
        ) => {
            const { id, value } = action.payload
            const item = state.fields.find((field) => field.id === id)
            if (item) {
                item.value = value
                item.errorMessage = ''
            }
            if (id === ProgramAttributes.OWNER) {
                const subscribersField = state.fields.find(
                    (field) => field.id === ProgramAttributes.SUBSCRIBER,
                )

                if (subscribersField) {
                    subscribersField.value = Array.isArray(
                        subscribersField.value,
                    )
                        ? subscribersField.value.filter(
                              (i: IListItemValue) =>
                                  i.id !== (value as IListItemValue)?.id,
                          )
                        : []
                    subscribersField.excludeOrgs = [
                        (value as IListItemValue)?.id,
                    ]
                }
                const carriersField = state.fields.find(
                    (field) => field.id === ProgramAttributes.CARRIER,
                )

                if (carriersField) {
                    carriersField.value = Array.isArray(carriersField.value)
                        ? carriersField.value.filter(
                              (i: IListItemValue) =>
                                  i.id !== (value as IListItemValue)?.id,
                          )
                        : []
                    carriersField.excludeOrgs = [(value as IListItemValue)?.id]
                }
                const receiversField = state.fields.find(
                    (field) => field.id === ProgramAttributes.RECEIVER,
                )

                if (receiversField) {
                    receiversField.value = Array.isArray(receiversField.value)
                        ? receiversField.value.filter(
                              (i: IListItemValue) =>
                                  i.id !== (value as IListItemValue)?.id,
                          )
                        : []
                    receiversField.excludeOrgs = [(value as IListItemValue)?.id]
                }
                const customersField = state.fields.find(
                    (field) => field.id === ProgramAttributes.CUSTOMER,
                )

                if (customersField) {
                    customersField.value = Array.isArray(customersField.value)
                        ? customersField.value.filter(
                              (i: IListItemValue) =>
                                  i.id !== (value as IListItemValue)?.id,
                          )
                        : []
                    customersField.excludeOrgs = [(value as IListItemValue)?.id]
                }
            }
        },
        updateProgramTemplate: (
            state,
            action: PayloadAction<UpdateProgramTemplate>,
        ) => {
            const { categoryId, id, templateId, value } = action.payload
            const category = state.templatesData.find(
                (field) => field.categoryId === categoryId,
            )
            const template = category?.categoryTemplates.find(
                (i) => i.id === id,
            )
            if (template) {
                const allSubTemplateIds = getNestedIdsById(
                    template.templates,
                    templateId,
                )
                if (value) {
                    template.selectedTemplates = [
                        ...(template.selectedTemplates || []),
                        ...allSubTemplateIds,
                    ]
                    template.templates[0].errorMessage = ''
                } else {
                    template.selectedTemplates =
                        template.selectedTemplates.filter(
                            (i) => !allSubTemplateIds.includes(i),
                        )
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            setProgramData.fulfilled,
            (state, action: PayloadAction<any>) => {
                const { updatedProgramFields, updatedProgramTypeListData } =
                    action.payload
                state.fields = updatedProgramFields
                state.ProgramTypeListData = updatedProgramTypeListData
            },
        )
        builder.addCase(
            updateProgramTemplatesWithSubTemplates.fulfilled,
            (state, action: PayloadAction<ProgramTemplatesType[]>) => {
                state.templatesData = action.payload
            },
        )
    },
})

export const {
    updateProgramFields,
    updateProgramTemplates,
    updateProgramField,
    updateProgramTemplate,
} = programSlice.actions
export default programSlice.reducer
